import { useCallback, useMemo, useState } from 'react';

export interface DepthExtremes {
  min: number;
  max: number;
}

/**
 * Shared depth window for the side-by-side log tracks. Each track chart
 * reports zoom/pan via `afterSetExtremes`; the resulting window is fed
 * back into every track spec so all tracks stay depth-aligned.
 */
export const useSyncedDepthExtremes = (fullMin: number, fullMax: number) => {
  const [window, setWindow] = useState<DepthExtremes | null>(null);

  const handleAfterSetExtremes = useCallback(
    (event: { min?: number; max?: number; trigger?: string }) => {
      if (event.trigger !== 'zoom' && event.trigger !== 'pan') return;
      if (event.min === undefined || event.max === undefined) {
        setWindow(null);
        return;
      }
      setWindow({ min: event.min, max: event.max });
    },
    []
  );

  const reset = useCallback(() => setWindow(null), []);

  const extremes = useMemo<DepthExtremes>(
    () => ({
      min: window?.min ?? fullMin,
      max: window?.max ?? fullMax
    }),
    [window, fullMin, fullMax]
  );

  return { extremes, isZoomed: window !== null, handleAfterSetExtremes, reset };
};
